import React, { useState, useEffect } from 'react';
import { Search, QrCode, Plus, X, Loader2, Edit2, Trash2, ExternalLink, CreditCard, Building } from 'lucide-react';

export default function PaymentMethodManagement() {
  const [methodsList, setMethodsList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [tableLoading, setTableLoading] = useState(false);

  // Pagination Parameters
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Modal Controls
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [qrFile, setQrFile] = useState(null);

  // Form State
  const [paymentForm, setPaymentForm] = useState({
    title: '',
    upi_id: '',
    account_holder_name: '',
    bank_name: '',
    account_number: '',
    ifsc_code: '',
    qr_code_image: ''
  });

  useEffect(() => {
    fetchMethods();
  }, [searchTerm]);

  const fetchMethods = async () => {
    setTableLoading(true);
    try {
      const res = await fetch(`https://metapi.emedhatechnologies.com/api/payment?search=${encodeURIComponent(searchTerm)}`);
      const result = await res.json();
      if (result.success) setMethodsList(result.data);
      else setMethodsList([]);
    } catch (err) {
      console.error(err);
      setMethodsList([]);
    } finally {
      setTableLoading(false);
    }
  };

  const handleOpenAddModal = () => {
    setEditingId(null);
    setQrFile(null);
    setPaymentForm({
      title: '',
      upi_id: '',
      account_holder_name: '',
      bank_name: '',
      account_number: '',
      ifsc_code: '',
      qr_code_image: ''
    });
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (item) => {
    setEditingId(item.id);
    setQrFile(null);
    setPaymentForm({
      title: item.title,
      upi_id: item.upi_id,
      account_holder_name: item.account_holder_name || '',
      bank_name: item.bank_name || '',
      account_number: item.account_number || '',
      ifsc_code: item.ifsc_code || '',
      qr_code_image: item.qr_code_image || ''
    });
    setIsModalOpen(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('payload', JSON.stringify(paymentForm));
    if (qrFile) {
      formData.append('qr_image', qrFile);
    }

    const endpoint = editingId ? `update-method/${editingId}` : 'add-method';
    const method = editingId ? 'PUT' : 'POST';

    try {
      const res = await fetch(`https://metapi.emedhatechnologies.com/api/payment/${endpoint}`, {
        method: method,
        body: formData
      });
      const result = await res.json();
      if (result.success) {
        setIsModalOpen(false);
        fetchMethods();
      } else {
        alert(result.message || "Operation failed.");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteEntry = async (id) => {
    if (!window.confirm("Are you sure you want to remove this payment method?")) return;
    try {
      const res = await fetch(`https://metapi.emedhatechnologies.com/api/payment/delete-method/${id}`, {
        method: 'PUT'
      });
      if ((await res.json()).success) fetchMethods();
    } catch (err) {
      console.error(err);
    }
  };

  // Pagination computations
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = methodsList.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(methodsList.length / itemsPerPage);

  return (
    <div style={styles.container}>
      
      {/* TOP HEADER CTA BAR */}
      <div style={styles.topActionBar}>
        <div>
          <h2 style={styles.mainTitle}>Payment Gateway & UPI Methods</h2>
          <p style={styles.mainSubtitle}>Configure UPI IDs, QR Code assets, and Bank accounts for student fee collections</p>
        </div>
        <button onClick={handleOpenAddModal} style={styles.addBtn}>
          <Plus size={15} style={{ marginRight: '6px' }} /> Add New Payment Method
        </button>
      </div>

      {/* SEARCH STRIP */}
      <div style={styles.filterControlPanel}>
        <div style={styles.searchWrapper}>
          <Search size={16} style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search by title, UPI ID, or holder name..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            style={styles.tableSearchInput}
          />
        </div>
        <div style={styles.totalIndicator}>Active Gateway Methods: <strong style={{ color: '#1c6ec9' }}>{methodsList.length}</strong></div>
      </div>

      {/* MASTER DATA TABLE */}
      {tableLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} color="#1c6ec9" />
        </div>
      ) : (
        <div style={{ overflowX: 'auto', width: '100%' }}>
          <table style={styles.dataTable}>
            <thead>
              <tr>
                <th style={styles.tableTh}>QR Image</th>
                <th style={styles.tableTh}>Gateway Title & Holder</th>
                <th style={styles.tableTh}>UPI Identifier (VPA)</th>
                <th style={styles.tableTh}>Bank Details (Optional)</th>
                <th style={styles.tableTh}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {methodsList.length === 0 ? (
                <tr><td colSpan="5" style={styles.noDataTd}>No payment methods added yet.</td></tr>
              ) : (
                currentItems.map((item) => (
                  <tr key={item.id} style={styles.tableTr}>
                    <td style={styles.tableTd}>
                      {item.qr_code_image ? (
                        <a href={item.qr_code_image} target="_blank" rel="noreferrer">
                          <img src={item.qr_code_image} alt="QR Code" style={styles.qrThumbnail} />
                        </a>
                      ) : (
                        <div style={styles.noQrPlaceholder}><QrCode size={18} color="#94a3b8" /></div>
                      )}
                    </td>
                    <td style={styles.tableTd}>
                      <strong style={{ color: '#1c6ec9', fontSize: '13.5px', display: 'block' }}>{item.title}</strong>
                      <span style={{ fontSize: '11.5px', color: '#64748b' }}>Holder: {item.account_holder_name || 'N/A'}</span>
                    </td>
                    <td style={styles.tableTd}>
                      <span style={styles.upiBadge}>
                        <CreditCard size={12} style={{ marginRight: '4px' }} /> {item.upi_id}
                      </span>
                    </td>
                    <td style={styles.tableTd}>
                      {item.bank_name ? (
                        <div style={{ fontSize: '12px', color: '#334155' }}>
                          <Building size={11} style={{ marginRight: '3px' }} /> <strong>{item.bank_name}</strong>
                          <br /><span style={{ color: '#64748b' }}>A/C: {item.account_number} | IFSC: {item.ifsc_code}</span>
                        </div>
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: '12px' }}>UPI Only</span>
                      )}
                    </td>
                    <td style={styles.tableTd}>
                      <div style={styles.actionBtnGroup}>
                        <button onClick={() => handleOpenEditModal(item)} style={styles.editRowBtn} title="Edit Method"><Edit2 size={12} /> Edit</button>
                        <button onClick={() => handleDeleteEntry(item.id)} style={styles.deleteRowBtn} title="Delete Method"><Trash2 size={12} /> Delete</button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* PAGINATION TOOLBAR */}
      {methodsList.length > 0 && (
        <div style={styles.paginationDock}>
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            style={currentPage === 1 ? { ...styles.pageBtn, ...styles.pageBtnDisabled } : styles.pageBtn}
          >
            Previous
          </button>
          <span style={styles.pageIndicator}>
            Page <strong>{currentPage}</strong> of {totalPages || 1}
          </span>
          <button
            disabled={currentPage === totalPages || totalPages === 0}
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            style={currentPage === totalPages || totalPages === 0 ? { ...styles.pageBtn, ...styles.pageBtnDisabled } : styles.pageBtn}
          >
            Next
          </button>
        </div>
      )}

      {/* MODAL POPUP WINDOW */}
      {isModalOpen && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modalWrapperCard}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>
                {editingId ? 'Modify Payment Gateway Entry' : 'Add New Payment Gateway Entry'}
              </h3>
              <button onClick={() => setIsModalOpen(false)} style={styles.modalCloseX}><X size={18} /></button>
            </div>

            <form onSubmit={handleFormSubmit} style={styles.modalForm}>
              
              <div style={styles.formSplitGrid}>
                <div style={styles.fieldContainer}>
                  <label style={styles.fieldLabel}>Gateway Title Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. GPay / PhonePe / Paytm UPI"
                    value={paymentForm.title}
                    onChange={(e) => setPaymentForm({ ...paymentForm, title: e.target.value })}
                    style={styles.formInput}
                  />
                </div>

                <div style={styles.fieldContainer}>
                  <label style={styles.fieldLabel}>UPI VPA ID *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. metacademy@upi"
                    value={paymentForm.upi_id}
                    onChange={(e) => setPaymentForm({ ...paymentForm, upi_id: e.target.value })}
                    style={styles.formInput}
                  />
                </div>
              </div>

              <div style={styles.fieldContainer}>
                <label style={styles.fieldLabel}>Account Holder Name (Optional)</label>
                <input
                  type="text"
                  placeholder="e.g. Edulearner of Higher Education"
                  value={paymentForm.account_holder_name}
                  onChange={(e) => setPaymentForm({ ...paymentForm, account_holder_name: e.target.value })}
                  style={styles.formInput}
                />
              </div>

              {/* BANK DETAILS BLOCK (OPTIONAL) */}
              <div style={{ background: '#f8fafc', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <label style={{ ...styles.fieldLabel, color: '#1c6ec9', marginBottom: '8px', display: 'block' }}>
                  Bank Account Transfer Details (Optional)
                </label>
                <div style={styles.formSplitGrid}>
                  <div style={styles.fieldContainer}>
                    <input
                      type="text"
                      placeholder="Bank Name (e.g. HDFC Bank)"
                      value={paymentForm.bank_name}
                      onChange={(e) => setPaymentForm({ ...paymentForm, bank_name: e.target.value })}
                      style={{ ...styles.formInput, fontSize: '12.5px' }}
                    />
                  </div>
                  <div style={styles.fieldContainer}>
                    <input
                      type="text"
                      placeholder="Account Number"
                      value={paymentForm.account_number}
                      onChange={(e) => setPaymentForm({ ...paymentForm, account_number: e.target.value })}
                      style={{ ...styles.formInput, fontSize: '12.5px' }}
                    />
                  </div>
                </div>
                <div style={{ ...styles.fieldContainer, marginTop: '8px' }}>
                  <input
                    type="text"
                    placeholder="IFSC Code (e.g. HDFC0001234)"
                    value={paymentForm.ifsc_code}
                    onChange={(e) => setPaymentForm({ ...paymentForm, ifsc_code: e.target.value })}
                    style={{ ...styles.formInput, fontSize: '12.5px' }}
                  />
                </div>
              </div>

              {/* QR CODE FILE UPLOAD */}
              <div style={styles.fieldContainer}>
                <label style={styles.fieldLabel}>Upload UPI QR Code Image (PNG/JPG - Optional)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setQrFile(e.target.files[0])}
                  style={styles.formFileInput}
                />
              </div>

              <div style={styles.formActionDock}>
                <button type="button" onClick={() => setIsModalOpen(false)} style={styles.formCancelBtn}>Cancel</button>
                <button type="submit" style={styles.formSaveBtn}>Save Payment Gateway</button>
              </div>

            </form>
          </div>
        </div>
      )}

    </div>
  );
}

// FORMATTING STYLES MATRIX
const styles = {
  container: { width: '100%', boxSizing: 'border-box' },
  topActionBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' },
  mainTitle: { margin: 0, fontSize: '20px', fontWeight: '800', color: '#0f172a' },
  mainSubtitle: { margin: '2px 0 0 0', fontSize: '13px', color: '#64748b' },
  addBtn: { background: 'linear-gradient(90deg, #e30504 0%, #a80302 100%)', border: 'none', borderRadius: '12px', color: '#fff', padding: '12px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' },
  filterControlPanel: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: '16px', flexWrap: 'wrap', borderBottom: '1px solid #edf2f7', paddingBottom: '14px' },
  searchWrapper: { position: 'relative', display: 'flex', alignItems: 'center', flex: 1, maxWidth: '340px', width: '100%' },
  searchIcon: { position: 'absolute', left: '12px', color: '#94a3b8' },
  tableSearchInput: { width: '100%', padding: '9px 12px 9px 36px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '9px', color: '#0f172a', fontSize: '13.5px', outline: 'none' },
  totalIndicator: { fontSize: '14.5px', fontWeight: '600', color: '#334155' },
  dataTable: { width: '100%', borderCollapse: 'collapse', textAlign: 'left' },
  tableTh: { padding: '12px 14px', borderBottom: '2px solid #e2e8f0', color: '#64748b', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', whiteSpace: 'nowrap' },
  tableTr: { borderBottom: '1px solid #f1f5f9', background: '#ffffff' },
  tableTd: { padding: '12px 14px', color: '#475569', fontSize: '13px', verticalAlign: 'middle', whiteSpace: 'nowrap' },
  noDataTd: { padding: '30px 14px', textAlign: 'center', color: '#94a3b8', fontSize: '14px', fontWeight: '500' },
  upiBadge: { background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', padding: '4px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', display: 'inline-flex', alignItems: 'center' },
  qrThumbnail: { width: '48px', height: '48px', borderRadius: '6px', objectFit: 'cover', border: '1px solid #cbd5e1' },
  noQrPlaceholder: { width: '48px', height: '48px', borderRadius: '6px', background: '#f8fafc', border: '1px dashed #cbd5e1', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  actionBtnGroup: { display: 'flex', gap: '6px', alignItems: 'center' },
  editRowBtn: { background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', padding: '5px 10px', borderRadius: '6px', fontSize: '11.5px', fontWeight: '600', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '3px' },
  deleteRowBtn: { background: '#fff1f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '5px 10px', borderRadius: '6px', fontSize: '11.5px', fontWeight: '600', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '3px' },
  modalBackdrop: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.25)', backdropFilter: 'blur(8px)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  modalWrapperCard: { background: '#ffffff', border: '1px solid rgba(255,255,255,0.8)', borderRadius: '20px', width: '100%', maxWidth: '580px', maxHeight: '90vh', padding: '24px', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.05)', overflowY: 'auto' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '12px', borderBottom: '1px solid #e2e8f0' },
  modalCloseX: { background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer' },
  modalForm: { display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '14px' },
  formSplitGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  fieldContainer: { display: 'flex', flexDirection: 'column', gap: '4px' },
  fieldLabel: { color: '#64748b', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' },
  formInput: { width: '100%', padding: '9px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#0f172a', fontSize: '13px', outline: 'none', boxSizing: 'border-box' },
  formFileInput: { fontSize: '12px', color: '#475569', padding: '6px 0' },
  formActionDock: { display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '8px', borderTop: '1px solid #e2e8f0', paddingTop: '12px' },
  formCancelBtn: { background: 'transparent', border: '1px solid #e2e8f0', color: '#64748b', padding: '9px 16px', borderRadius: '8px', cursor: 'pointer' },
  formSaveBtn: { background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)', border: 'none', color: '#fff', padding: '9px 16px', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' },
  paginationDock: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px', marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #edf2f7' },
  pageIndicator: { fontSize: '13px', color: '#475569' },
  pageBtn: { background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '6px 14px', color: '#334155', fontSize: '12.5px', fontWeight: '600', cursor: 'pointer' },
  pageBtnDisabled: { background: '#f8fafc', color: '#94a3b8', cursor: 'not-allowed', borderColor: '#e2e8f0' }
};